import React, { useState, useRef } from 'react';
import { X, Upload, File } from 'lucide-react';
import { useCreateFileMutation } from '../../store/Reducer/file';
import toast from 'react-hot-toast';

const UploadModal = ({ isOpen, onClose, mediaType, onSuccess }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [files, setFiles] = useState([]);
    const fileInputRef = useRef(null);
    const [createFile, { isLoading }] = useCreateFileMutation();

    const handleDragEnter = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFiles = Array.from(e.dataTransfer.files);
        setFiles(droppedFiles);
    };

    const handleFileInput = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setFiles(selectedFiles);
    };

    const handleUpload = async () => {
        try {
            if (files.length > 0) {
                const response = await createFile(files[0]).unwrap();
                console.log('File uploaded successfully', response);
                toast.success(response?.message || 'File uploaded successfully');
                onSuccess?.();
                onClose();
                setFiles([]);
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            toast.error(error?.data?.message || 'Error uploading file');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white rounded-lg px-8 py-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold text-gray-800">Upload {mediaType}</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X className="w-6 h-6" />
                    </button>
                </div>
                <div
                    className={`border-2 border-dashed rounded-lg p-8 mb-6 text-center ${
                        isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                    } ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
                    onDragEnter={!isLoading ? handleDragEnter : undefined}
                    onDragLeave={!isLoading ? handleDragLeave : undefined}
                    onDragOver={!isLoading ? handleDragOver : undefined}
                    onDrop={!isLoading ? handleDrop : undefined}
                    onClick={() => !isLoading && fileInputRef.current.click()}
                >
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileInput}
                        className="hidden"
                        multiple
                        disabled={isLoading}
                    />
                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-2">
                        {isLoading ? 'Upload in progress...' : 'Drag and drop your files here, or'}
                    </p>
                    <button
                        className={`text-blue-500 font-medium ${
                            isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:text-blue-600'
                        }`}
                        disabled={isLoading}
                    >
                        Browse files
                    </button>
                </div>
                {files.length > 0 && (
                    <div className="mb-6">
                        <h3 className="font-medium mb-2">Selected files:</h3>
                        <ul className="space-y-2">
                            {files.map((file, index) => (
                                <li key={index} className="flex items-center text-sm text-gray-600">
                                    <File className="w-4 h-4 mr-2" />
                                    {file.name}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                <div className="flex justify-end">
                    <button
                        onClick={handleUpload}
                        disabled={isLoading}
                        className={`px-4 py-2 bg-blue-600 text-white rounded-md transition-colors ${
                            isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
                        }`}
                    >
                        {isLoading ? (
                            <div className="flex items-center">
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                Uploading...
                            </div>
                        ) : (
                            'Upload'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UploadModal;

